'use client';

import { type KeyboardEvent, startTransition, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { AUTOCOMPLETE_MIN_PARTIAL_LENGTH, autocompleteV3 } from '@/src/lib/api/endpoints/area';
import type { AreaResponse, AutocompleteAreaType, AutocompleteResult, AutocompleteSuggestion } from '@/src/lib/api';
import { useRecentAreas } from '@/src/lib/hooks/area/useRecentAreas';
import { emitTrackSuggestedAreaEvent } from '@/src/analytics/hotpads/events';

const DEBOUNCE_MS = 200;
const CACHE_TTL_MS = 5 * 60 * 1000;
const NEAR_ME_VALUE = '__near_me__';
// Minimum typed characters before suggestions are fetched — and before Enter is
// allowed to search the top suggestion. Tracks the endpoint's own floor so the
// fetch, the options list, and the Enter handler can't drift from it.
const MIN_QUERY_LENGTH = AUTOCOMPLETE_MIN_PARTIAL_LENGTH;

const searchCache = new Map<string, { results: Array<AutocompleteSuggestion>; timestamp: number }>();

/**
 * One empty array for every "no suggestions" state. A fresh `[]` would be a new identity, so
 * `setResults([])` re-rendered on each keystroke below the fetch threshold even though nothing
 * had changed - and that fed the options memo, re-rendering Constellation's listbox (HP-9111).
 */
const NO_RESULTS: ReadonlyArray<AutocompleteSuggestion> = [];

// Types displayed in ListingSearch: every area kind V3 surfaces (cities,
// neighborhoods, boroughs, counties, zips, schools) plus listings (rental addresses).
const LISTING_SEARCH_TYPES = ['city', 'neighborhood', 'borough', 'county', 'zip', 'school', 'address'];

// Suggestions are already filtered and ranked server-side, so keep every option
// the API returned. Constellation's default filter keeps an option only if its
// label/meta substring-matches the typed text; a canonical address label
// ("555 Bryant St, San Francisco, CA") rarely contains exactly what the user
// typed (zip, "St" vs "Street", abbreviations), so the default would silently
// drop address results that the API did return.
export const keepAllOptions = () => true;

/** Where a selection is reported from in clickstream. */
const LOCATION_ANALYTICS_CONTEXTS = {
  homepage: { triggerLocation: 'hp_homepage', triggerObject: 'hp_home_page_component|search' },
  search: { triggerLocation: 'search_results', triggerObject: 'filter_panel' },
  notFound: { triggerLocation: 'page_not_found', triggerObject: '404_page|search' },
} as const;

export type LocationAnalyticsSurface = keyof typeof LOCATION_ANALYTICS_CONTEXTS;

export type OptionShape = {
  value: string;
  label: string;
  meta?: string;
  groupHeading: string;
  result: AutocompleteResult;
};

export type OptionGroup = { heading: string; items: Array<OptionShape> };

/**
 * What an Enter keypress should do. `blocked` and `fallthrough` are genuinely
 * different: typed-but-unresolved text must never reach a wrapping form's
 * implicit submission (it would search the previously selected area), whereas
 * committed text should fall through to exactly that.
 */
export type EnterResolution = { kind: 'search'; option: OptionShape } | { kind: 'blocked' } | { kind: 'fallthrough' };

/** Exported so a caller's own Enter handling uses the same floor as the fetch. */
export const LOCATION_MIN_QUERY_LENGTH = MIN_QUERY_LENGTH;

export function formatAreaLabel(area: AreaResponse | AutocompleteResult | null | undefined): string {
  if (!area) return '';
  // Only a city reads as "City, ST"; every other area's `city` is its parent, not itself.
  if (area.type?.toLowerCase() === 'city' && area.city && area.state) return `${area.city}, ${area.state}`;
  return area.name ?? '';
}

/** Translation key for the dropdown group heading of an area suggestion. */
function areaTypeHeadingKey(
  areaType: AutocompleteAreaType | null | undefined,
): 'neighborhoods' | 'boroughs' | 'counties' | 'zipCodes' | 'schools' | 'cities' {
  switch (areaType) {
    case 'neighborhood':
      return 'neighborhoods';
    case 'county':
      return 'counties';
    case 'borough':
      return 'boroughs';
    case 'zip':
      return 'zipCodes';
    case 'school':
      return 'schools';
    default:
      return 'cities';
  }
}

/**
 * Map a V3 suggestion onto the `AutocompleteResult` shape the rest of
 * ListingSearch (setArea, area display) still speaks. Listings navigate by
 * `lotIdEncoded`; areas by `resourceId`.
 */
function suggestionToResult(s: AutocompleteSuggestion): AutocompleteResult {
  return {
    type: s.type === 'listing' ? 'address' : (s.areaType ?? 'city'),
    name: s.label,
    resourceId: s.resourceId ?? undefined,
    lotIdEncoded: s.lotIdEncoded ?? undefined,
    city: s.city ?? undefined,
    county: s.county ?? undefined,
    state: s.state ?? undefined,
  };
}

function areaToResult(area: AreaResponse | null | undefined): AutocompleteResult | null {
  if (!area?.resourceId || !area.name) return null;
  return {
    type: 'City',
    name: area.name,
    resourceId: area.resourceId,
    city: area.city,
    state: area.state,
    county: area.county,
  };
}

/**
 * Bucket options under their heading, keeping groups in first-appearance order
 * (Near Me before Recently Viewed; for results, whatever rank the API returned
 * each type in) and items in their original order within a group.
 *
 * Only the full-screen dialog renders headings — Constellation's dropdown gets
 * the flat list it has always been given.
 */
export function groupByHeading(items: Array<OptionShape>): Array<OptionGroup> {
  const groups: Array<OptionGroup> = [];
  const byHeading = new Map<string, OptionGroup>();

  for (const item of items) {
    let group = byHeading.get(item.groupHeading);
    if (!group) {
      group = { heading: item.groupHeading, items: [] };
      byHeading.set(item.groupHeading, group);
      groups.push(group);
    }
    group.items.push(item);
  }

  return groups;
}

export type UseLocationSuggestionsArgs = {
  /**
   * Seeds the visible label and re-syncs it when the parent's area settles.
   * Pass `null` for a field that should start empty — a formatted area label is
   * longer than `MIN_QUERY_LENGTH`, so seeding one puts the options list on the
   * results branch with nothing fetched, i.e. an empty list.
   */
  area: AreaResponse | AutocompleteResult | null;
  nearMeArea?: AreaResponse | null;
  onSelect: (result: AutocompleteResult) => void;
  onEnterSearch?: (result: AutocompleteResult) => void;
  onUserInput?: () => void;
  analyticsSurface: LocationAnalyticsSurface;
};

/**
 * Everything the location autocomplete does that isn't presentation: the
 * debounced V3 fetch and its cache, recent areas, the Near Me affordance,
 * Constellation's grouped option shape, and the HP-8246 Enter contract.
 *
 * Extracted so the inline field and the full-screen dialog (HP-8998) can differ
 * in how they present suggestions without sharing a component whose props have
 * to describe both. Callers own their own Combobox composition and spread the
 * returned handlers onto it.
 */
export function useLocationSuggestions({
  area,
  nearMeArea,
  onSelect,
  onEnterSearch,
  onUserInput,
  analyticsSurface,
}: UseLocationSuggestionsArgs) {
  const tSearch = useTranslations('search');
  const { recentAreas } = useRecentAreas();
  const reactId = useId();
  const idPrefix = `loc-combobox-${reactId.replace(/:/g, '')}`;
  const areaLabel = formatAreaLabel(area);

  const [inputValue, setInputValue] = useState(areaLabel);
  const [results, setResults] = useState<ReadonlyArray<AutocompleteSuggestion>>(NO_RESULTS);
  const [loading, setLoading] = useState(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // True while the input shows a label produced by a selection (or the
  // area-sync below) rather than text the user typed. Enter on committed text
  // belongs to the form's fallback submission (draftArea is already correct);
  // Enter on typed text must resolve through the suggestions instead.
  const inputCommittedRef = useRef(true);

  // Re-sync the visible label when the parent's `area` changes (e.g. live mode
  // after navigation lands and `useCurrentArea()` settles).
  useEffect(() => {
    setInputValue(areaLabel);
    inputCommittedRef.current = true;
  }, [areaLabel]);

  const nearMeResult = useMemo(() => areaToResult(nearMeArea), [nearMeArea]);

  /**
   * Only whether the query is long enough, not the text itself. Every option's content comes from
   * `results`, `nearMeResult` and `recentAreas`, so keying the memo below on `inputValue` minted a
   * fresh `options` array on every keystroke while the debounced fetch had not yet changed
   * `results` - and a new array identity re-renders Constellation's whole listbox inside the
   * interaction task. On the homepage that is the dominant cost of typing (HP-9111).
   */
  const hasQuery = inputValue.trim().length >= MIN_QUERY_LENGTH;

  const options = useMemo<Array<OptionShape>>(() => {
    const items: Array<OptionShape> = [];

    if (!hasQuery) {
      if (nearMeResult) {
        items.push({
          value: NEAR_ME_VALUE,
          label: nearMeResult.city ?? nearMeResult.name ?? tSearch('nearMe'),
          meta: tSearch('nearMe'),
          groupHeading: tSearch('nearMe'),
          result: nearMeResult,
        });
      }
      for (const recent of recentAreas) {
        if (!recent.resourceId) continue;
        if (nearMeResult?.resourceId === recent.resourceId) continue;
        items.push({
          value: recent.resourceId,
          label: formatAreaLabel(recent) || recent.name || recent.resourceId,
          meta: [recent.county, recent.state].filter(Boolean).join(', ') || undefined,
          groupHeading: tSearch('recentlyViewed'),
          result: { ...recent, type: 'City' as const },
        });
      }
      return items;
    }

    for (const s of results) {
      const isListing = s.type === 'listing';
      const navValue = isListing ? s.lotIdEncoded : s.resourceId;
      if (!navValue) continue;
      // The option `value` must be a stable identifier that never coincides
      // with the typed text: Constellation marks an option `selected` when its
      // value equals the current input and then suppresses that option's
      // select event. A bare zip's resourceId IS the query string ("94110"),
      // so key off the suggestion's opaque `id` — or its `regionId` once the
      // BE's area migration omits `id` on region suggestions — before falling
      // back to navValue.
      const optionValue = s.id ?? (s.regionId != null ? String(s.regionId) : navValue);
      items.push({
        value: optionValue,
        label: s.label,
        meta: [s.county, s.state].filter(Boolean).join(', ') || undefined,
        groupHeading: isListing ? tSearch('properties') : tSearch(areaTypeHeadingKey(s.areaType)),
        result: suggestionToResult(s),
      });
    }
    return items;
  }, [hasQuery, nearMeResult, recentAreas, results, tSearch]);

  const handleInputValueChange = useCallback(
    (next: string) => {
      onUserInput?.();
      setInputValue(next);
      inputCommittedRef.current = false;
    },
    [onUserInput],
  );

  // Debounced autocomplete fetch once the user has typed MIN_QUERY_LENGTH chars.
  useEffect(() => {
    const trimmed = inputValue.trim();

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    // Urgent, unlike `setLoading(true)` below: this runs once per fetch rather
    // than once per keystroke, and deferring it would let a keydown land between
    // the results arriving and the commit, leaving the Enter handler reading the
    // previous query's options.
    const settle = (next: ReadonlyArray<AutocompleteSuggestion>) => {
      setResults(next);
      setLoading(false);
    };

    if (trimmed.length < MIN_QUERY_LENGTH) {
      settle(NO_RESULTS);
      return;
    }

    // A committed label (selection or area sync) isn't a user query — skip the
    // fetch so selecting "Denver, CO" doesn't immediately re-search that label.
    // Keeps `results`, so re-opening the panel still shows them.
    if (inputCommittedRef.current) {
      setLoading(false);
      return;
    }

    startTransition(() => setLoading(true));

    const controller = new AbortController();
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const cacheKey = trimmed.toLowerCase();
        const cached = searchCache.get(cacheKey);
        const now = Date.now();
        if (cached && now - cached.timestamp < CACHE_TTL_MS) {
          settle(cached.results);
          return;
        }
        const response = await autocompleteV3(trimmed, {
          limit: 10,
          types: LISTING_SEARCH_TYPES,
          signal: controller.signal,
        });
        searchCache.set(cacheKey, { results: response, timestamp: now });
        settle(response);
      } catch {
        if (!controller.signal.aborted) settle(NO_RESULTS);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      controller.abort();
    };
  }, [inputValue]);

  // Holds the result Constellation selected during the current Enter
  // keystroke (its onOptionSelect fires synchronously inside the keydown
  // dispatch), so the bubble-phase handler below can search it immediately.
  // Cleared at capture phase so click-selections can't leak into a later
  // Enter press.
  const enterSelectionRef = useRef<AutocompleteResult | null>(null);

  // Selection side effects shared by Constellation-driven selections and the
  // Enter-on-typed-text path below.
  const selectOption = useCallback(
    (option: OptionShape) => {
      // Emitted inline, not deferred: the clickstream envelope is stamped when
      // `event()` runs (timestamp + page context), so delaying it past the
      // navigation would attribute this selection to the destination page.
      emitTrackSuggestedAreaEvent(LOCATION_ANALYTICS_CONTEXTS[analyticsSurface]);
      onSelect(option.result);
      setInputValue(option.label);
      inputCommittedRef.current = true;
    },
    [onSelect, analyticsSurface],
  );

  const handleOptionSelect = useCallback(
    (option: unknown) => {
      if (!option || typeof option !== 'object') return;
      const o = option as { value?: unknown };
      const value = typeof o.value === 'string' ? o.value : null;
      if (!value) return;
      const match = options.find((item) => item.value === value);
      if (!match) return;
      selectOption(match);
      enterSelectionRef.current = match.result;
    },
    [options, selectOption],
  );

  /**
   * The HP-8246 Enter rule, in one place. Both presentations call it — the
   * inline field with whatever Constellation highlighted, the dialog with its
   * own `aria-activedescendant` option — so the rule can't drift between them.
   */
  const resolveEnter = useCallback(
    (highlighted?: OptionShape | null, top?: OptionShape): EnterResolution => {
      if (highlighted) return { kind: 'search', option: highlighted };
      // A committed label already staged the right area; let the form submit it.
      if (inputCommittedRef.current) return { kind: 'fallthrough' };
      if (inputValue.trim().length < MIN_QUERY_LENGTH) return { kind: 'fallthrough' };
      // `top` comes from the caller because the two presentations don't order
      // suggestions the same way — the dropdown shows the API's ranking, the
      // dialog groups them under headings — and Enter has to search the row
      // that's actually first on screen.
      //
      // Typed text with no suggestion yet (debounce/network) or no match:
      // swallow the keystroke rather than search an area the user didn't type.
      return top ? { kind: 'search', option: top } : { kind: 'blocked' };
    },
    [inputValue],
  );

  const handleEnterCapture = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter') return;
    // An IME composition commit is not a search gesture: stop it before
    // Constellation's own Enter handling selects an option. (No preventDefault
    // — browsers don't implicit-submit composing Enters, and the IME still
    // needs its default handling.)
    if (event.nativeEvent.isComposing) {
      event.stopPropagation();
      return;
    }
    // Holding Enter auto-repeats keydowns; only the first press may search or
    // submit, so swallow repeats entirely (preventDefault also blocks the
    // form's implicit submission).
    if (event.repeat) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    enterSelectionRef.current = null;
  }, []);

  // Runs after Constellation's own input keydown handler (bubble phase).
  // Enter must run the search in the same keystroke (HP-8246):
  // - If Constellation selected a highlighted option during this keydown
  //   (it preventDefaults Enter whenever its dropdown is open — see
  //   use-combobox.js `onKeyDownInputEnter`), search that result.
  // - Otherwise, typed-but-unresolved text searches the top-ranked suggestion.
  //   This is decided off `inputCommittedRef`, NOT `event.defaultPrevented`,
  //   so it also covers keystrokes Constellation never handles: numpad Enter
  //   (its switch keys off event.code, and 'NumpadEnter' matches no case) and
  //   Enter after the dropdown already closed on a previous dead keystroke.
  // - Committed text (a selection's label) falls through to the wrapping
  //   form's implicit submission, which searches the already-staged draftArea.
  const handleEnterKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Enter' || !onEnterSearch) return;
      // Constellation already selected this one during the keydown, so it only
      // needs searching — not re-selecting.
      const selected = enterSelectionRef.current;
      enterSelectionRef.current = null;
      if (selected) {
        onEnterSearch(selected);
        return;
      }

      const resolution = resolveEnter(null, options[0]);
      if (resolution.kind === 'fallthrough') return;
      // Typed text: never let this keystroke reach the form, whether or not
      // there's a suggestion to search.
      event.preventDefault();
      if (resolution.kind === 'blocked') return;
      selectOption(resolution.option);
      onEnterSearch(resolution.option.result);
    },
    [onEnterSearch, options, resolveEnter, selectOption],
  );

  /**
   * Select, then run the search for it — the Enter path for a caller that owns
   * its own highlight (the full-screen list) rather than delegating to
   * Constellation's dropdown.
   */
  const searchOption = useCallback(
    (option: OptionShape) => {
      selectOption(option);
      onEnterSearch?.(option.result);
    },
    [onEnterSearch, selectOption],
  );

  return {
    idPrefix,
    inputValue,
    loading,
    /**
     * Flat and in the order the API ranked them — the shape Constellation's
     * `Combobox` takes directly. A caller that renders its own list can group
     * these with `groupByHeading`.
     */
    options,
    handleInputValueChange,
    handleOptionSelect,
    handleEnterCapture,
    handleEnterKeyDown,
    selectOption,
    searchOption,
    resolveEnter,
  };
}
